import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAppSelector } from 'src/utils/Hooks';
import { useBaseTopicRequestBuilder } from '../../utils/useBaseTopicRequestBuilder';
import { TopicRequestBuilder } from '../../utils/TopicRequestBuilder';
import { weekIdToTimestamp } from 'src/utils/dateUtils';
import { useSplineComparisonDateRange, useSplineMainDateRange } from 'src/utils/chartUtils';
import { TopicAdSummaryMetrics } from './types';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';

export interface GetAdSummaryMetricsResponseRecord extends TopicAdSummaryMetrics {
  weekId: number;
}

export interface GetAdSummaryMetricsResponse {
  records: GetAdSummaryMetricsResponseRecord[];
}

/**
 * Returns all the metrics for topics paid search page
 */
export const useAdSummaryMetrics = () => {
  const { startWeek, endWeek } = useSplineMainDateRange();
  const { comparisonStartWeek, comparisonEndWeek } = useSplineComparisonDateRange();

  const retailerId = useAppSelector((state) => state.retailer.id);

  const baseRequest = useBaseTopicRequestBuilder();

  const adSummaryMetricsRequest = baseRequest
    .setStartWeekId(startWeek)
    .setEndWeekId(endWeek)
    .setRetailerId(+retailerId)
    .build();

  const adSummaryMetricsComparisonRequest = new TopicRequestBuilder(adSummaryMetricsRequest)
    .setStartWeekId(comparisonStartWeek)
    .setEndWeekId(comparisonEndWeek)
    .build();

  const { data, isLoading } = useQuery({
    queryKey: [TOPIC_QUERY_KEYS.all, 'GetAdSummaryMetrics', adSummaryMetricsRequest, adSummaryMetricsComparisonRequest],
    queryFn: async () => {
      const endpoint = '/api/beacontopics/GetAdSummaryMetrics';
      const [{ data: response }, { data: comparisonResponse }] = await Promise.all([
        axios.post<GetAdSummaryMetricsResponse>(endpoint, adSummaryMetricsRequest),
        axios.post<GetAdSummaryMetricsResponse>(endpoint, adSummaryMetricsComparisonRequest)
      ]);

      return [response, comparisonResponse];
    }
  });

  const response = data ?? [{ records: [] }, { records: [] }];

  const getPrimarySplineData = (fieldId: keyof TopicAdSummaryMetrics) => {
    return response[0].records
      .sort((a, b) => a.weekId - b.weekId)
      .map((record) => [weekIdToTimestamp(record.weekId), record[fieldId]]);
  };

  const getComparisonSplineData = (fieldId: keyof TopicAdSummaryMetrics) => {
    return response[1].records
      .sort((a, b) => a.weekId - b.weekId)
      .map((record) => [weekIdToTimestamp(record.weekId), record[fieldId]]);
  };

  return {
    response,
    isLoading,
    getPrimarySplineData,
    getComparisonSplineData
  };
};
